import React, { useState, useEffect, useMemo } from 'react';
import Classes from './RecordTable.module.css';
import useScreenWidth from '../../../../../../Hooks/useScreenWidth';
import SkeletonLoading from '../../../../../GlobalComponents/SkeletonLoading/SkeletonLoading';
import { Pagination, Stack } from '@mui/material';
import TableRow from './TableRow';
import { useSelector } from 'react-redux';
import backend from '../../../../../../api';

function fmtDate(d) {
  if (!d) return '-';
  const x = new Date(d);
  return x.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
function money(n, c = 'USD') {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: 'currency', currency: (c || 'USD').toUpperCase() });
}
function titleCase(s) {
  return String(s || '').replace(/\b\w/g, m => m.toUpperCase());
}

export default function RecordTable() {
  const { user } = useSelector(s => s?.auth || {});
  const userId = user?._id;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 480;

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await backend.get(`/api/invoices?userId=${userId}`);
        const raw = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
        if (alive) setItems(raw);
      } catch (e) {
        console.error('Fetch invoices error:', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId]);

  // map invoices to table view model (Total shows NET after refund)
  const rows = useMemo(() => {
    return (items || []).map(inv => {
      const refunded = Math.max(0, Number(inv?.refundedTotal || 0));
      const total = Math.max(0, Number(inv?.total || 0));
      const net = Math.max(0, total - refunded);

      return {
        _id: inv._id,
        raw: inv,
        Invoice: inv.title || 'Mobile Fuel Delivery',
        Date: fmtDate(inv.issuedAt),
        DateDue: fmtDate(inv.dueAt),
        Total: money(net, inv.currency),          // net total visible in list
        Status: titleCase(inv.status),
        refundedTotal: refunded,                  // pass for "Refunded" pill
        currency: inv.currency || 'USD',
      };
    });
  }, [items]);

  const totalPages = Math.ceil(rows.length / itemsPerPage) || 1;
  const currentItems = rows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, rows.length);
  const totalItems = rows.length;

  function handlePageChange(e, pageNum) {
    setCurrentPage(pageNum);
  }

  const paidCount = items.filter(x => String(x.status).toLowerCase() === 'completed').length;
  const unpaidCount = items.length - paidCount;

  return (
    <div className={Classes.hero}>
      <div className={Classes.table}>
        <div className={Classes.tableWrapper}>

          <div className={Classes.tableBanner}>
            <div className='flexAlign10'>
              <div className={Classes.tableBannerItem}>
                <h5>Paid</h5>
                <h4>{paidCount}</h4>
              </div>
              <div className={Classes.tableBannerItem}>
                <h5>Unpaid</h5>
                <h4>{unpaidCount}</h4>
              </div>
              <div className={Classes.tableBannerItem}>
                <h5 className={Classes.allText}>All</h5>
                <h4>{paidCount + unpaidCount}</h4>
              </div>
            </div>
            <div />
          </div>

          <div className={Classes.tableHeader}>
            <div className={`${Classes.headerItem} ${Classes.header1}`}><h5>Invoice</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header2}`}><h5>Date</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header3}`}><h5>Due Date</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header4}`}><h5>Total</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header5}`}><h5>Status</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header6}`} />
          </div>

          <div className={Classes.tableBody}>
            {loading ? (
              <SkeletonLoading length={itemsPerPage} height={isMobile ? '3.5rem' : '2.8rem'} width="100%" br="6px" padding='0' />
            ) : currentItems && currentItems.length > 0 ? (
              currentItems.map((row, idx) => (
                <TableRow key={row._id} value={row} index={idx} />
              ))
            ) : (
              <h5 className='placeholderText' style={{ margin: '15px 19px' }} >No invoices yet...</h5>
            )}
          </div>

          { currentItems && currentItems.length > 0 &&
            <div className={Classes.pagFooter}>
              <h5 style={{ height: '1.2rem' }}>
                {loading
                  ? <SkeletonLoading height='1.2rem' width='10rem' br='4px' padding='0' />
                  : `Showing ${startItem} to ${endItem} of ${totalItems} results`
                }
              </h5>
              <Stack spacing={2} className={Classes.pagination}>
                {loading
                  ? <SkeletonLoading height='1.2rem' width='5rem' br='4px' padding='0' />
                  : <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} shape="rounded" />
                }
              </Stack>
            </div>
          }
        </div>
      </div>
    </div>
  );
}
